import { isValid, parseISO } from 'date-fns';
import { useLocation, useNavigate } from 'react-router';

import { TermedLevelState } from '../../common/termedLevel/types';
import { type BookingOverviewFilters, emptyBookingOverviewFilters } from './bookingOverviewFilters';

type BookingOverviewUrlState = { filters: BookingOverviewFilters; search: string };

const filterParameters = ['resource', 'fleetGroup', 'period', 'status', 'free', 'withoutRenewal', 'endsFrom', 'endsTo'];
const uniqueValues = (params: URLSearchParams, key: string) => [...new Set(params.getAll(key).filter(Boolean))];
const readDate = (value: string | null) =>
    value && /^\d{4}-\d{2}-\d{2}$/.test(value) && isValid(parseISO(value)) ? value : undefined;

export function readBookingOverviewUrlState(search: string): BookingOverviewUrlState {
    const params = new URLSearchParams(search);
    return {
        filters: {
            resources: uniqueValues(params, 'resource').flatMap(value => {
                const separator = value.indexOf(':');
                return separator > 0 && separator < value.length - 1
                    ? [JSON.stringify([value.slice(0, separator), value.slice(separator + 1)])]
                    : [];
            }),
            fleetGroups: uniqueValues(params, 'fleetGroup'),
            periods: uniqueValues(params, 'period').filter(value => /^\d+:(days|months|years)$/.test(value)),
            statuses: uniqueValues(params, 'status').filter(
                (value): value is TermedLevelState =>
                    value === TermedLevelState.ACTIVE || value === TermedLevelState.PENDING
            ),
            freeOnly: params.get('free') === 'true',
            withoutRenewal: params.get('withoutRenewal') === 'true',
            endsFrom: readDate(params.get('endsFrom')),
            endsTo: readDate(params.get('endsTo')),
        },
        search: params.get('q') ?? '',
    };
}

export function writeBookingOverviewUrlState(currentSearch: string, { filters, search }: BookingOverviewUrlState) {
    const params = new URLSearchParams(currentSearch);
    for (const key of [...filterParameters, 'q']) {
        params.delete(key);
    }
    for (const resource of filters.resources) {
        const [type, id] = JSON.parse(resource) as [string, string];
        params.append('resource', `${type}:${id}`);
    }
    for (const [key, values] of [
        ['fleetGroup', filters.fleetGroups],
        ['period', filters.periods],
        ['status', filters.statuses],
    ] as const) {
        for (const value of values) {
            params.append(key, value);
        }
    }
    if (filters.freeOnly) {
        params.set('free', 'true');
    }
    if (filters.withoutRenewal) {
        params.set('withoutRenewal', 'true');
    }
    if (filters.endsFrom) {
        params.set('endsFrom', filters.endsFrom);
    }
    if (filters.endsTo) {
        params.set('endsTo', filters.endsTo);
    }
    if (search) {
        params.set('q', search);
    }
    return params.toString();
}

export function useBookingOverviewUrlState() {
    const location = useLocation();
    const navigate = useNavigate();
    const state = readBookingOverviewUrlState(location.search);
    const update = (nextState: BookingOverviewUrlState, replace = false) => {
        const search = writeBookingOverviewUrlState(location.search, nextState);
        if (search !== location.search.replace(/^\?/, '')) {
            navigate({ search, hash: location.hash }, { replace, state: location.state, preventScrollReset: true });
        }
    };

    return {
        ...state,
        setFilters: (filters: BookingOverviewFilters) => update({ ...state, filters }),
        // Search uses replace so each keystroke does not create a history entry.
        setSearch: (search: string) => update({ ...state, search }, true),
        clearFilters: () => update({ filters: emptyBookingOverviewFilters, search: '' }),
    };
}
